import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import useActivityInput, {
  ActivityInputValues,
  defaultActivityInputValues,
} from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import { ActivityType } from 'models/Activity';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionType } from 'models/QuestionTemplate';
import { activityQueryKeys, createActivity, ProgressUpdatePayload } from 'services/api/activity';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { episodeQueryKeys } from 'services/api/episode';
import { progressChartQueryKeys } from 'services/api/locationEpisode/progressChart';
import { createLocationEpisodeAdmission } from 'services/api/locationEpisodeAdmissions';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { useToastActions } from 'stores/toastStore';

import QuestionList, { AnswerType, QuestionListRefType } from './QuestionList/QuestionList';

export type DischargeModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
  episode: Episode;
  title: string;
  invalidateData?: () => void;
  linkedRecords?: LocationEpisode[];
};

type UniqueTemplate = {
  templateId: string;
  questions: QuestionType[];
  locationEpisodeIds: string[];
  displayNames: string[];
};

function groupByTemplate(locationEpisodes: LocationEpisode[]): UniqueTemplate[] {
  const templateMap = new Map<string, UniqueTemplate>();

  locationEpisodes.forEach((le) => {
    const contentKey = le.progressTemplate.contentKey;
    const existing = templateMap.get(contentKey);
    const displayName = le.owner.client?.name || le.group.name;

    if (existing) {
      existing.locationEpisodeIds.push(le.id);
      existing.displayNames.push(displayName);
    } else {
      templateMap.set(contentKey, {
        templateId: contentKey,
        questions: le.progressTemplate.questions,
        locationEpisodeIds: [le.id],
        displayNames: [displayName],
      });
    }
  });

  return Array.from(templateMap.values());
}

export default function PatientStatusUpdateModal(props: DischargeModalType) {
  const { setShow, patientName, locationEpisode, title, invalidateData, episode, linkedRecords = [] } = props;
  const questionsRef = useRef<QuestionListRefType>(null);
  const [admittedDate, setAdmittedDate] = useState<Date | null>(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput();
  const { translate } = useTranslation();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const hasSharedRecordsFlag = profile.hasFlag('shared_records');

  // Only include LEs the user can actually create a progress update on. This excludes
  // LEs the user manages (e.g., SNF CC building user's own SNF CC patient — they can't
  // create a progress update on their managed record, but they can on linked upstream
  // records). Discharged LEs are already filtered upstream.
  const progressUpdateLEs = useMemo(() => {
    const primary = profile.canCreateProgressUpdate(episode, locationEpisode) ? [locationEpisode] : [];
    const linked = linkedRecords.filter(
      (le) =>
        le.id !== locationEpisode.id &&
        profile.canCreateProgressUpdate(new Episode({ id: le.episodeId, owner: le.owner }), le)
    );
    return [...primary, ...linked];
  }, [episode, locationEpisode, linkedRecords, profile]);

  // Multi-step wizard state
  const isSharedFlow = linkedRecords.length > 0;
  const uniqueTemplates = isSharedFlow ? groupByTemplate(progressUpdateLEs) : [];
  const totalSteps = uniqueTemplates.length;
  const isSingleTemplate = totalSteps <= 1;

  const [currentStep, setCurrentStep] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState<Map<string, AnswerType[]>>(new Map());
  const [collectedNotes, setCollectedNotes] = useState<Map<string, ActivityInputValues>>(new Map());

  const currentTemplate = isSharedFlow ? uniqueTemplates[currentStep] : null;
  const isLastStep = !isSharedFlow || currentStep === totalSteps - 1;

  const currentStepLocationEpisode = useMemo(() => {
    if (!currentTemplate) return locationEpisode;
    const leId = currentTemplate.locationEpisodeIds[0];
    return [locationEpisode, ...linkedRecords].find((le) => le.id === leId) ?? locationEpisode;
  }, [currentTemplate, locationEpisode, linkedRecords]);

  const isAdmitting =
    locationEpisode.currentRehabState.queue || (isSharedFlow && linkedRecords.some((lr) => lr.inQueue));

  const { mutate: mutateActivity, isPending: isActivityPending } = useMutation({
    mutationFn: createActivity,
  });

  const { mutate: mutateAdmission, isPending: isAdmissionPending } = useMutation({
    mutationFn: createLocationEpisodeAdmission,
  });

  const isPending = isActivityPending || isAdmissionPending;

  const cancel = () => {
    questionsRef.current?.reset();
    resetNote();
    setAllQuestionsAnswered(false);
    setAdmittedDate(null);
    setCurrentStep(0);
    setCollectedAnswers(new Map());
    setCollectedNotes(new Map());
    setShow(false);
  };

  const saveCurrentStepData = () => {
    if (!currentTemplate) return;
    const answers = questionsRef.current?.getAnswers() ?? [];
    setCollectedAnswers((prev) => {
      const next = new Map(prev);
      next.set(currentTemplate.templateId, answers);
      return next;
    });
    if (!isSingleTemplate) {
      setCollectedNotes((prev) => {
        const next = new Map(prev);
        next.set(currentTemplate.templateId, { ...note });
        return next;
      });
    }
  };

  const restoreStepNotes = (stepIndex: number) => {
    if (isSingleTemplate) return;
    const template = uniqueTemplates[stepIndex];
    const savedNote = collectedNotes.get(template.templateId);
    if (savedNote) {
      setNote(savedNote);
    } else {
      resetNote();
    }
  };

  const handleNext = () => {
    saveCurrentStepData();
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setAllQuestionsAnswered(false);
    restoreStepNotes(nextStep);
  };

  const handleBack = () => {
    saveCurrentStepData();
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    restoreStepNotes(prevStep);
  };

  const handleSuccess = () => {
    // only invalidate episode and location episode if the progress update results in the start of treatment
    if (!locationEpisode.currentRehabState.inTreatment) {
      queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show({ id: locationEpisode.episodeId }) });
    }
    queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) });
    queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
    queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count() });
    queryClient.invalidateQueries({ queryKey: progressChartQueryKeys.show(locationEpisode.id) });
    invalidateData?.();
    cancel();
  };

  const buildProgressUpdates = (): ProgressUpdatePayload[] => {
    if (!isSharedFlow) {
      return [
        {
          locationEpisodeId: locationEpisode.id,
          questions: questionsRef.current!.getAnswers(),
          ...getSerializedNoteValues(),
        },
      ];
    }

    if (isSingleTemplate) {
      const answers = questionsRef.current!.getAnswers();
      const serializedNote = getSerializedNoteValues();
      return uniqueTemplates[0].locationEpisodeIds.map((leId) => ({
        locationEpisodeId: leId,
        questions: answers,
        ...serializedNote,
      }));
    }

    saveCurrentStepData();
    const updates: ProgressUpdatePayload[] = [];
    uniqueTemplates.forEach((template) => {
      const answers =
        template.templateId === currentTemplate?.templateId
          ? (questionsRef.current?.getAnswers() ?? [])
          : (collectedAnswers.get(template.templateId) ?? []);
      const templateNote =
        template.templateId === currentTemplate?.templateId
          ? note
          : (collectedNotes.get(template.templateId) ?? defaultActivityInputValues);

      template.locationEpisodeIds.forEach((leId) => {
        updates.push({ locationEpisodeId: leId, questions: answers, ...getSerializedNoteValues(templateNote) });
      });
    });
    return updates;
  };

  const submit = () => {
    const mutationCallbacks = {
      onSuccess: handleSuccess,
      onError: () => addToast({ text: 'Something went wrong. Please try again.' }),
    };

    if (!hasSharedRecordsFlag) {
      // TODO: OLIO-2027 - Remove this branch once all admissions use the admission endpoint.
      mutateActivity(
        {
          ...getSerializedNoteValues(),
          type: ActivityType.PROGRESS_UPDATE,
          questions: questionsRef.current!.getAnswers(),
          enteredAt: admittedDate ?? undefined,
          locationEpisodeId: locationEpisode.id,
        },
        mutationCallbacks
      );
      return;
    }

    const progressUpdates = buildProgressUpdates();

    if (isAdmitting) {
      mutateAdmission(
        { locationEpisodeId: locationEpisode.id, enteredAt: admittedDate ?? undefined, progressUpdates },
        mutationCallbacks
      );
    } else {
      mutateActivity(
        { type: ActivityType.PROGRESS_UPDATE, progressUpdates, locationEpisodeId: locationEpisode.id },
        mutationCallbacks
      );
    }
  };

  const latestStatus = locationEpisode.latestProgressUpdate?.data?.questions?.find((q) => q.kind === 'status')?.answer
    .text;

  const admittedDateRequired = locationEpisode?.currentRehabState.queue && !admittedDate;
  const submitDisabled = isUploading || !allQuestionsAnswered;
  const nextDisabled = !allQuestionsAnswered || admittedDateRequired;

  const questions: QuestionType[] = useMemo(() => {
    const baseQuestions =
      isSharedFlow && currentTemplate ? currentTemplate.questions : locationEpisode.progressTemplate.questions;

    if (!isSharedFlow || !currentTemplate) return baseQuestions;

    const savedAnswers = collectedAnswers.get(currentTemplate.templateId);
    if (!savedAnswers?.length) return baseQuestions;

    return savedAnswers;
  }, [isSharedFlow, currentTemplate, locationEpisode.progressTemplate.questions, collectedAnswers]);

  const subtitle = useMemo(() => {
    if (!isSharedFlow || isSingleTemplate || !currentTemplate) return undefined;
    const uniqueNames = [...new Set(currentTemplate.displayNames)];
    const label = uniqueNames.length > 1 ? 'Records' : 'Record';
    return `${label}: ${uniqueNames.join(', ')}`;
  }, [isSharedFlow, isSingleTemplate, currentTemplate]);

  return (
    <StandardModal title={title} subtitle={subtitle} onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        {locationEpisode?.dischargeOverdue && (
          <Alert style={{ marginBottom: 16 }}>
            <Alert.Text>
              Patient&apos;s projected {translate('discharge').toLowerCase()} date of&nbsp;
              <b>{simpleDate(locationEpisode.projectedDischargeReview?.date)}</b>
              &nbsp; has passed. Please provide details regarding any barriers to discharge.
            </Alert.Text>
          </Alert>
        )}
        {locationEpisode?.currentRehabState.queue && (!isSharedFlow || currentStep === 0) && (
          <InputGroup title={`Actual ${translate('Admission')} Date`}>
            <DatePicker
              data-cy='actualAdmissionDate'
              placeholder='Select Date'
              maxDate={new Date()}
              selected={admittedDate}
              onChange={setAdmittedDate}
            />
          </InputGroup>
        )}
        <QuestionList
          key={isSharedFlow ? currentStep : undefined}
          ref={questionsRef}
          questions={questions}
          setAllQuestionsAnswered={setAllQuestionsAnswered}
          latestStatus={latestStatus}
          locationEpisode={currentStepLocationEpisode}
          episode={episode}
          noteValues={note}
          setNoteValues={setNote}
          resetNoteValues={resetNote}
          setIsUploading={setIsUploading}
          taggable={!isSharedFlow}
        />
      </FormContainer>
      <Actions>
        {isSharedFlow && currentStep > 0 ? (
          <Button variant='ghost' onClick={handleBack}>
            Back
          </Button>
        ) : (
          <Button variant='ghost' onClick={cancel}>
            Cancel
          </Button>
        )}
        {isLastStep ? (
          <Button onClick={submit} disabled={submitDisabled} loading={isPending}>
            {isSharedFlow && !isSingleTemplate ? 'Submit All' : 'Submit'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={nextDisabled}>
            Next
          </Button>
        )}
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  padding-bottom: 20px;
`;
